import ReactDOM from "react-dom/client";
import "./index.css";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import App from "./App";
import { AppConfigProvider } from "AppConfigProvider";
import initializeI18n from "./i18nInit";
import "react-vertical-timeline-component/style.min.css";
import "react-quill/dist/quill.snow.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "@livekit/components-styles";
import TagManager from 'react-gtm-module';

const tagManagerArgs = {
    gtmId: 'GTM-WPN2VBML',
    dataLayer: {
      server_container_url: 'https://gtm.qcall.ai'
    }
}

/* ══════════════════════════════════════════════════════════════════════════════
   QCall Brand Theme — Chakra UI Configuration
   Based on QCall Brand Guidelines
   ══════════════════════════════════════════════════════════════════════════════ */
const theme = extendTheme({
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
  },

  /* ── Brand Fonts ── */
  fonts: {
    heading: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
    body: "'Eleven', 'Roboto', 'Arial', sans-serif",
    mono: "'Roboto Mono', monospace",
  },

  /* ── Brand Colors ── */
  colors: {
    /* Core Neutrals */
    gray: {
      50: "#F2F2F2",
      100: "#E5E5E5",
      200: "#DCDCDC",
      300: "#BDBDBD",
      400: "#949494",
      500: "#767676",
      600: "#6E6E6E",
      700: "#525252",
      800: "#464646",
      900: "#3D3D3D",
    },
    /* Primary (Purple) */
    purple: {
      50: "#FBF6FE",
      100: "#F5EBFC",
      200: "#EBD5F9",
      300: "#D8ADF3",
      400: "#C07FEB",
      500: "#A855E0",
      600: "#8A35C8",
      700: "#6D20A5",
      800: "#521882",
      900: "#32123E",
    },
    /* Red */
    red: {
      50: "#FEF3F2",
      100: "#FDE5E3",
      200: "#FCCAC6",
      300: "#F9A29B",
      400: "#F57068",
      500: "#EC4338",
      600: "#D92B20",
      700: "#B72018",
      800: "#971D17",
      900: "#440D0B",
    },
    /* Orange */
    orange: {
      50: "#FFF7ED",
      100: "#FFEDD5",
      200: "#FED7AA",
      300: "#FDBA74",
      400: "#FB923C",
      500: "#F97316",
      600: "#EA580C",
      700: "#C2410C",
      800: "#9A3412",
      900: "#492104",
    },
    /* Green */
    green: {
      50: "#ECFDF4",
      100: "#D1FAE4",
      200: "#A7F3CE",
      300: "#6EE7B2",
      400: "#34D392",
      500: "#10B97B",
      600: "#059764",
      700: "#047953",
      800: "#066043",
      900: "#022C20",
    },
    /* Blue */
    blue: {
      50: "#F2F5FC",
      100: "#E3E9F8",
      200: "#CDD6F3",
      300: "#A9BAEB",
      400: "#8095E0",
      500: "#6274D7",
      600: "#4E5ACA",
      700: "#454AB8",
      800: "#3D3F96",
      900: "#252846",
    },
    /* Teal */
    teal: {
      50: "#EAFDF9",
      100: "#D0FAF2",
      200: "#A5F4E5",
      300: "#6CE9D4",
      400: "#35D4BF",
      500: "#18B9A6",
      600: "#109588",
      700: "#11776E",
      800: "#135F59",
      900: "#072C2B",
    },
    /* Cyan */
    cyan: {
      50: "#EEFBFD",
      100: "#D5F4FA",
      200: "#B1E8F4",
      300: "#7CD6EC",
      400: "#3FBBDC",
      500: "#239FC5",
      600: "#1E80A6",
      700: "#206887",
      800: "#22566F",
      900: "#0D2F3F",
    },
    /* Yellow */
    yellow: {
      50: "#FCFCEA",
      100: "#F9F7C5",
      200: "#F5F08E",
      300: "#EDE34D",
      400: "#E3D31E",
      500: "#D4BC12",
      600: "#B7940D",
      700: "#926D0E",
      800: "#795614",
      900: "#3E240A",
    },
    /* Brand / Primary */
    brand: {
      50: "#FBF6FE",
      100: "#F5EBFC",
      200: "#EBD5F9",
      300: "#D8ADF3",
      400: "#C07FEB",
      500: "#A855E0",
      600: "#8A35C8",
      700: "#6D20A5",
      800: "#521882",
      900: "#32123E",
    },
  },

  /* ── Brand Typography Styles ── */
  textStyles: {
    display: {
      fontSize: "3rem",
      fontWeight: "700",
      lineHeight: "1.1",
      letterSpacing: "-0.02em",
      fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
    },
    h1: {
      fontSize: "2.25rem",
      fontWeight: "700",
      lineHeight: "1.2",
      letterSpacing: "-0.02em",
      fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
    },
    h2: {
      fontSize: "1.875rem",
      fontWeight: "700",
      lineHeight: "1.25",
      letterSpacing: "-0.01em",
      fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: "700",
      lineHeight: "1.3",
      letterSpacing: "-0.01em",
      fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: "500",
      lineHeight: "1.35",
      fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: "500",
      lineHeight: "1.4",
      fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: "500",
      lineHeight: "1.4",
      fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
    },
    "body-lg": {
      fontSize: "1.125rem",
      fontWeight: "400",
      lineHeight: "1.6",
      letterSpacing: "0.01em",
    },
    body: {
      fontSize: "1rem",
      fontWeight: "400",
      lineHeight: "1.6",
      letterSpacing: "0.01em",
    },
    "body-sm": {
      fontSize: "0.875rem",
      fontWeight: "400",
      lineHeight: "1.5",
      letterSpacing: "0.01em",
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: "400",
      lineHeight: "1.5",
      letterSpacing: "0.02em",
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: "500",
      lineHeight: "1.5",
      letterSpacing: "0.08em",
      textTransform: "uppercase" as const,
    },
  },

  /* ── Brand Radius ── */
  radii: {
    none: "0",
    sm: "4px",
    base: "8px",
    md: "8px",
    lg: "12px",
    xl: "20px",
    "2xl": "28px",
    full: "999px",
  },

  /* ── Brand Shadows ── */
  shadows: {
    xs: "0 1px 2px rgba(0,0,0,0.05)",
    sm: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
    base: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
    md: "0 4px 6px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)",
    lg: "0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
    xl: "0 20px 25px rgba(0,0,0,0.10), 0 8px 10px rgba(0,0,0,0.04)",
    "2xl": "0 25px 50px rgba(0,0,0,0.15)",
    outline: "0 0 0 2px #7C53FC",
  },

  /* ── Brand Borders ── */
  borders: {
    none: "none",
    "1px": "1px solid #E5E5E5",
    "2px": "2px solid #292929",
  },

  /* ── Brand Spacing (4px base) ── */
  space: {
    "0.5": "2px",
    "1": "4px",
    "1.5": "6px",
    "2": "8px",
    "3": "12px",
    "4": "16px",
    "5": "20px",
    "6": "24px",
    "8": "32px",
    "10": "40px",
    "12": "48px",
    "16": "64px",
    "20": "80px",
    "24": "96px",
  },

  /* ── Brand Z-Index ── */
  zIndices: {
    overlay: 1200,
    modal: 1300,
    popover: 1400,
    toast: 1500,
    tooltip: 1600,
  },

  /* ── Component Styles ── */
  components: {
    /* ── Button ── */
    Button: {
      baseStyle: {
        fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
        fontWeight: "500",
        borderRadius: "8px",
        letterSpacing: "0.01em",
        transition: "all 150ms ease-in-out",
        _focus: {
          boxShadow: "0 0 0 2px #7C53FC",
          outline: "2px solid transparent",
        },
        _focusVisible: {
          boxShadow: "0 0 0 2px #7C53FC",
          outline: "2px solid var(--qc-bx, #7C53FC)",
          outlineOffset: "2px",
        },
      },
      sizes: {
        xs: {
          h: "28px",
          fontSize: "0.75rem",
          px: "8px",
        },
        sm: {
          h: "32px",
          fontSize: "0.75rem",
          px: "12px",
          letterSpacing: "0.02em",
        },
        md: {
          h: "40px",
          fontSize: "0.875rem",
          px: "16px",
        },
        lg: {
          h: "48px",
          fontSize: "1rem",
          px: "24px",
          borderRadius: "12px",
        },
        xl: {
          h: "56px",
          fontSize: "1rem",
          px: "32px",
          borderRadius: "20px",
        },
      },
      variants: {
        solid: {
          bg: "#7C53FC",
          color: "white",
          _hover: { bg: "#6840E0" },
          _active: { bg: "#5530C0" },
          _disabled: {
            bg: "#E5E5E5",
            color: "#949494",
            opacity: 1,
          },
        },
        outline: {
          bg: "white",
          color: "#292929",
          borderColor: "#DCDCDC",
          borderWidth: "1px",
          _hover: {
            bg: "#F2F2F2",
            borderColor: "#BDBDBD",
          },
        },
        ghost: {
          color: "#525252",
          _hover: {
            bg: "#F2F2F2",
            color: "#292929",
          },
        },
        danger: {
          bg: "#FE451D",
          color: "white",
          _hover: { bg: "#440D0B" },
        },
      },
      defaultProps: {
        variant: "solid",
        size: "md",
      },
    },

    /* ── Input ── */
    Input: {
      baseStyle: {
        field: {
          fontFamily: "'Eleven', 'Roboto', 'Arial', sans-serif",
          borderRadius: "8px",
          transition: "border-color 150ms ease-in-out",
        },
      },
      sizes: {
        sm: {
          field: {
            h: "32px",
            fontSize: "0.875rem",
            px: "10px",
            borderRadius: "8px",
          },
        },
        md: {
          field: {
            h: "40px",
            fontSize: "0.875rem",
            px: "12px",
            borderRadius: "8px",
          },
        },
        lg: {
          field: {
            h: "48px",
            fontSize: "1rem",
            px: "16px",
            borderRadius: "12px",
          },
        },
      },
      variants: {
        outline: (props: any) => ({
          field: {
            bg: props.colorMode === "dark" ? "#0b1437" : "white",
            borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.1)" : "#DCDCDC",
            color: props.colorMode === "dark" ? "white" : "#292929",
            _hover: { borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.2)" : "#BDBDBD" },
            _focus: {
              borderColor: "transparent",
              boxShadow: "none",
            },
            _invalid: {
              borderColor: "#FE451D",
              borderWidth: "1px",
            },
            _disabled: {
              bg: props.colorMode === "dark" ? "#111c44" : "#F2F2F2",
              color: "#949494",
              borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.05)" : "#E5E5E5",
              opacity: 1,
            },
            _placeholder: {
              color: "#767676",
            },
          },
        }),
      },
      defaultProps: {
        variant: "outline",
        size: "md",
      },
    },

    /* ── Textarea ── */
    Textarea: {
      baseStyle: {
        fontFamily: "'Eleven', 'Roboto', 'Arial', sans-serif",
        borderRadius: "8px",
        transition: "border-color 150ms ease-in-out",
      },
      variants: {
        outline: (props: any) => ({
          bg: props.colorMode === "dark" ? "#0b1437" : "white",
          borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.1)" : "#DCDCDC",
          color: props.colorMode === "dark" ? "white" : "#292929",
          _hover: { borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.2)" : "#BDBDBD" },
          _focus: {
            borderColor: "transparent",
            boxShadow: "none",
          },
          _placeholder: {
            color: "#767676",
          },
        }),
      },
    },

    /* ── Select ── */
    Select: {
      baseStyle: {
        field: {
          fontFamily: "'Eleven', 'Roboto', 'Arial', sans-serif",
          borderRadius: "8px",
        },
      },
      variants: {
        outline: (props: any) => ({
          field: {
            borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.1)" : "#DCDCDC",
            color: props.colorMode === "dark" ? "white" : "#292929",
            bg: props.colorMode === "dark" ? "#0b1437" : "white",
            _hover: { borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.2)" : "#BDBDBD" },
            _focus: {
              borderColor: "transparent",
              boxShadow: "none",
            },
          },
        }),
      },
    },

    /* ── Card ── */
    Card: {
      baseStyle: (props: any) => ({
        container: {
          bg: props.colorMode === "dark" ? "#111c44" : "white",
          color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
          borderRadius: "12px",
          border: props.colorMode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid #E5E5E5",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
          padding: "24px",
        },
      }),
    },

    /* ── Modal ── */
    Modal: {
      baseStyle: (props: any) => ({
        dialog: {
          borderRadius: "20px",
          bg: props.colorMode === "dark" ? "#0b1437" : "white",
          color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
          boxShadow: "0 20px 25px rgba(0,0,0,0.10), 0 8px 10px rgba(0,0,0,0.04)",
        },
        header: {
          color: props.colorMode === "dark" ? "white" : "#292929",
        },
        body: {
          color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
        },
        closeButton: {
          color: props.colorMode === "dark" ? "white" : "#292929",
        },
        overlay: {
          bg: "rgba(0, 0, 0, 0.60)",
        },
      }),
      sizes: {
        sm: { dialog: { maxW: "400px" } },
        md: { dialog: { maxW: "560px" } },
        lg: { dialog: { maxW: "720px" } },
        full: { dialog: { maxW: "90vw" } },
      },
    },

    /* ── Tooltip ── */
    Tooltip: {
      baseStyle: {
        bg: "#292929",
        color: "white",
        fontSize: "0.75rem",
        fontWeight: "400",
        px: "10px",
        py: "6px",
        borderRadius: "8px",
        boxShadow: "0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
        maxW: "240px",
      },
    },

    /* ── Badge ── */
    Badge: {
      baseStyle: {
        fontSize: "0.75rem",
        fontWeight: "500",
        borderRadius: "999px",
        px: "8px",
        py: "2px",
        textTransform: "none",
      },
      variants: {
        subtle: {
          bg: "#F2F2F2",
          color: "#525252",
        },
        solid: {
          bg: "#7C53FC",
          color: "white",
        },
        outline: {
          borderColor: "#DCDCDC",
          color: "#525252",
        },
      },
    },

    /* ── Table ── */
    Table: {
      baseStyle: (props: any) => ({
        th: {
          bg: props.colorMode === "dark" ? "#1B254B" : "#F2F2F2",
          color: props.colorMode === "dark" ? "#BDBDBD" : "#525252",
          fontWeight: "500",
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          px: "12px",
          py: "16px",
          borderBottom: props.colorMode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid #E5E5E5",
          fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
        },
        td: {
          color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
          fontSize: "0.875rem",
          px: "12px",
          py: "16px",
          borderBottom: props.colorMode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid #E5E5E5",
        },
        tr: {
          _hover: {
            bg: props.colorMode === "dark" ? "#1B254B" : "#F2F2F2",
          },
        },
      }),
    },

    /* ── Drawer ── */
    Drawer: {
      baseStyle: (props: any) => ({
        dialog: {
          bg: props.colorMode === "dark" ? "#0b1437" : "white",
          color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
          borderRadius: "12px 0 0 12px",
        },
        header: {
          color: props.colorMode === "dark" ? "white" : "#292929",
          fontSize: "lg",
          fontWeight: "600",
        },
        body: {
          color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
        },
        closeButton: {
          color: props.colorMode === "dark" ? "white" : "#292929",
        },
        overlay: {
          bg: "rgba(0, 0, 0, 0.60)",
        },
      }),
    },

    /* ── Menu ── */
    Menu: {
      baseStyle: (props: any) => ({
        list: {
          bg: props.colorMode === "dark" ? "#1B254B" : "white",
          borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.1)" : "#E5E5E5",
          borderRadius: "12px",
          boxShadow: "0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
          py: "4px",
        },
        item: {
          fontSize: "0.875rem",
          color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
          px: "12px",
          py: "8px",
          borderRadius: "8px",
          mx: "4px",
          _hover: {
            bg: props.colorMode === "dark" ? "#111c44" : "#F2F2F2",
          },
          _focus: {
            bg: props.colorMode === "dark" ? "#111c44" : "#F2F2F2",
          },
        },
      }),
    },

    /* ── Popover ── */
    Popover: {
      baseStyle: (props: any) => ({
        content: {
          bg: props.colorMode === "dark" ? "#1B254B" : "white",
          borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.1)" : "#E5E5E5",
          borderRadius: "12px",
          boxShadow: "0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
        },
      }),
    },

    /* ── Tabs ── */
    Tabs: {
      baseStyle: {
        tab: {
          fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
          fontWeight: "500",
          fontSize: "0.875rem",
          color: "#525252",
          _selected: {
            color: "#7C53FC",
            borderColor: "#7C53FC",
          },
        },
      },
    },

    /* ── Slider ── */
    Slider: {
      baseStyle: {
        track: {
          bg: "#E5E5E5",
          borderRadius: "999px",
        },
        filledTrack: {
          bg: "#7C53FC",
        },
        thumb: {
          bg: "#7C53FC",
          borderColor: "#7C53FC",
          _focus: {
            boxShadow: "0 0 0 3px rgba(124, 83, 252, 0.3)",
          },
        },
      },
    },

    /* ── Switch ── */
    Switch: {
      baseStyle: {
        track: {
          bg: "#DCDCDC",
          _checked: {
            bg: "#7C53FC",
          },
        },
      },
    },

    /* ── Checkbox ── */
    Checkbox: {
      baseStyle: {
        control: {
          borderColor: "#DCDCDC",
          borderRadius: "4px",
          _checked: {
            bg: "#7C53FC",
            borderColor: "#7C53FC",
          },
          _focus: {
            boxShadow: "0 0 0 2px #7C53FC",
          },
        },
        label: {
          fontSize: "0.875rem",
          color: "#292929",
        },
      },
    },

    /* ── Radio ── */
    Radio: {
      baseStyle: {
        control: {
          borderColor: "#DCDCDC",
          _checked: {
            bg: "#7C53FC",
            borderColor: "#7C53FC",
          },
        },
        label: {
          fontSize: "0.875rem",
          color: "#292929",
        },
      },
    },

    /* ── Alert ── */
    Alert: {
      baseStyle: {
        container: {
          borderRadius: "12px",
          px: "16px",
          py: "12px",
        },
      },
      variants: {
        subtle: (props: any) => {
          const colorMap: Record<string, { bg: string; color: string }> = {
            success: { bg: "#ECFDF4", color: "#022C20" },
            error: { bg: "#FEF3F2", color: "#440D0B" },
            warning: { bg: "#FFF7ED", color: "#492104" },
            info: { bg: "#EEFBFD", color: "#0D2F3F" },
          };
          const colors = colorMap[props.status] || colorMap.info;
          return {
            container: { bg: colors.bg, color: colors.color },
          };
        },
      },
    },

    /* ── FormLabel ── */
    FormLabel: {
      baseStyle: (props: any) => ({
        fontSize: "0.875rem",
        fontWeight: "500",
        color: props.colorMode === "dark" ? "#BDBDBD" : "#525252",
        mb: "1",
      }),
    },

    /* ── Form ── */
    Form: {
      baseStyle: (props: any) => ({
        helperText: {
          fontSize: "0.75rem",
          color: props.colorMode === "dark" ? "#949494" : "#767676",
        },
      }),
    },

    /* ── Heading ── */
    Heading: {
      baseStyle: (props: any) => ({
        fontFamily: "'Eleven Semi Condensed', 'Roboto', 'Arial', sans-serif",
        color: props.colorMode === "dark" ? "white" : "#292929",
      }),
    },

    /* ── Text ── */
    Text: {
      baseStyle: (props: any) => ({
        fontFamily: "'Eleven', 'Roboto', 'Arial', sans-serif",
        color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
      }),
    },

    /* ── Divider ── */
    Divider: {
      baseStyle: (props: any) => ({
        borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.1)" : "#E5E5E5",
      }),
    },

    /* ── Skeleton ── */
    Skeleton: {
      baseStyle: {
        borderRadius: "8px",
        startColor: "#F2F2F2",
        endColor: "#DCDCDC",
      },
    },

    /* ── Progress ── */
    Progress: {
      baseStyle: {
        track: {
          bg: "#F2F2F2",
          borderRadius: "999px",
        },
        filledTrack: {
          bg: "#7C53FC",
          borderRadius: "999px",
        },
      },
    },

    /* ── Spinner ── */
    Spinner: {
      baseStyle: {
        color: "#7C53FC",
      },
    },

    /* ── Tag ── */
    Tag: {
      baseStyle: {
        container: {
          borderRadius: "999px",
          fontWeight: "500",
          fontSize: "0.75rem",
        },
      },
      variants: {
        subtle: (props: any) => {
          const colorMap: Record<string, { bg: string; color: string }> = {
            green: { bg: "#ECFDF4", color: "#047953" },
            red: { bg: "#FEF3F2", color: "#B72018" },
            purple: { bg: "#FBF6FE", color: "#6D20A5" },
            blue: { bg: "#F2F5FC", color: "#454AB8" },
            orange: { bg: "#FFF7ED", color: "#C2410C" },
            gray: { bg: "#F2F2F2", color: "#525252" },
          };
          const colors = colorMap[props.colorScheme] || { bg: "#F2F2F2", color: "#525252" };
          return { container: { bg: colors.bg, color: colors.color } };
        },
        solid: (props: any) => {
          const colorMap: Record<string, { bg: string; color: string }> = {
            green: { bg: "#10B97B", color: "white" },
            red: { bg: "#EC4338", color: "white" },
            purple: { bg: "#7C53FC", color: "white" },
            blue: { bg: "#6274D7", color: "white" },
            orange: { bg: "#F97316", color: "white" },
          };
          const colors = colorMap[props.colorScheme] || { bg: "#7C53FC", color: "white" };
          return { container: { bg: colors.bg, color: colors.color } };
        },
        outline: {
          container: {
            borderColor: "#DCDCDC",
            color: "#525252",
          },
        },
      },
    },

    /* ── Avatar ── */
    Avatar: {
      baseStyle: {
        container: {
          borderRadius: "999px",
        },
        excessLabel: {
          bg: "#F2F2F2",
          color: "#525252",
          borderRadius: "999px",
        },
      },
      sizes: {
        xs: { container: { w: "24px", h: "24px" }, label: { fontSize: "10px" } },
        sm: { container: { w: "32px", h: "32px" }, label: { fontSize: "12px" } },
        md: { container: { w: "40px", h: "40px" }, label: { fontSize: "14px" } },
        lg: { container: { w: "56px", h: "56px" }, label: { fontSize: "20px" } },
        xl: { container: { w: "80px", h: "80px" }, label: { fontSize: "28px" } },
      },
    },

    /* ── Breadcrumb ── */
    Breadcrumb: {
      baseStyle: {
        link: {
          fontSize: "0.875rem",
          color: "#525252",
          _hover: {
            color: "#292929",
            textDecoration: "none",
          },
        },
        separator: {
          color: "#949494",
        },
      },
    },
  },

  /* ── Global Styles ── */
  styles: {
    global: (props: any) => ({
      body: {
        bg: props.colorMode === "dark" ? "#0b1437" : "white",
        color: props.colorMode === "dark" ? "#F2F2F2" : "#292929",
        fontFamily: "'Eleven', 'Roboto', 'Arial', sans-serif",
        fontSize: "1rem",
        lineHeight: "1.6",
        letterSpacing: "0.01em",
      },
      "*, *::before, *::after": {
        borderColor: props.colorMode === "dark" ? "rgba(255,255,255,0.1)" : "#E5E5E5",
      },
      // "input:focus, select:focus, textarea:focus, input:focus-visible, select:focus-visible, textarea:focus-visible": {
      //   borderColor: "transparent !important",
      //   boxShadow: "none !important",
      //   outline: "none !important",
      // },
      a: {
        color: "#2857FF",
        _hover: {
          color: props.colorMode === "dark" ? "#CDD6F3" : "#252846",
        },
      },
    }),
  },
});

TagManager.initialize(tagManagerArgs)

// Initialize i18n
initializeI18n();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <ChakraProvider theme={theme}>
      <AppConfigProvider>
        <App />
      </AppConfigProvider>
    </ChakraProvider>
);
